import React, { useEffect, useState } from "react";
import { Link } from "react-scroll";
import { Row, Col, Card, Tab, Nav } from "react-bootstrap";
import axios from "axios";
import { useSelector } from "react-redux";
import { IMAGES } from "../../constant/theme";

const safeParse = (v, fb) => {
  try {
    return v ? JSON.parse(v) : fb;
  } catch {
    return fb;
  }
};

const ensureUniqueBy = (arr, getKey) => {
  const map = new Map();

  const toNumberPercent = (s) =>
    typeof s === "string" ? parseInt(s.replace("%", ""), 10) || 0 : 0;

  for (const item of arr) {
    if (!item) continue;
    const key = getKey(item);
    if (!key) continue;

    const existing = map.get(key);
    if (!existing) {
      map.set(key, item);
      continue;
    }

    const existingHasPic = !!existing.pic;
    const currentHasPic = !!item.pic;

    if (!existingHasPic && currentHasPic) {
      map.set(key, item);
      continue;
    }

    const existingScore = Number(existing.totalScore ?? existing.total ?? 0);
    const currentScore = Number(item.totalScore ?? item.total ?? 0);
    if (currentScore > existingScore) {
      map.set(key, item);
      continue;
    }

    const existingPercent = toNumberPercent(existing.score);
    const currentPercent = toNumberPercent(item.score);
    if (currentPercent > existingPercent) {
      map.set(key, item);
      continue;
    }
  }

  return Array.from(map.values());
};

const upsertByUserId = (arr, item) => {
  if (!item?.userId) return arr;
  const idx = arr.findIndex((x) => x.userId === item.userId);
  if (idx === -1) return [...arr, item];
  const copy = arr.slice();
  copy[idx] = { ...copy[idx], ...item };
  return copy;
};

const combineLeaderboards = (pred, goalsArr) => {
  const byId = new Map();

  for (const p of pred || []) {
    if (!p || !p.userId) continue;
    byId.set(p.userId, {
      userId: p.userId,
      email: p.email,
      title: p.title,
      pic: p.pic,
      rating: p.rating,
      score: p.score,
      totalScore: Number(p.total ?? 0),
    });
  }

  for (const g of goalsArr || []) {
    if (!g || !g.userId) continue;
    const prev = byId.get(g.userId) || {
      userId: g.userId,
      email: g.email,
      title: g.title,
      pic: g.pic,
      rating: g.rating,
      score: g.score,
      totalScore: 0,
    };
    const totalScore = (prev.totalScore || 0) + Number(g.total ?? 0);
    byId.set(g.userId, { ...prev, totalScore });
  }

  return Array.from(byId.values());
};

const mergeLeaderboardByUserId = (entries) => {
  const byUser = new Map();

  for (const item of entries || []) {
    if (!item) continue;
    const id = item.userId;
    if (!id) continue;

    const existing = byUser.get(id);
    if (!existing) {
      byUser.set(id, { ...item });
      continue;
    }

    const existingScore = Number(existing.totalScore || 0);
    const currentScore = Number(item.totalScore || 0);

    const primary = currentScore > existingScore ? item : existing;
    const secondary = currentScore > existingScore ? existing : item;

    const merged = { ...primary };

    if (!merged.email && secondary.email) merged.email = secondary.email;
    if (!merged.pic && secondary.pic) merged.pic = secondary.pic;
    if (!merged.title && secondary.title) merged.title = secondary.title;
    if (!merged.score && secondary.score) merged.score = secondary.score;

    byUser.set(id, merged);
  }

  return Array.from(byUser.values());
};

const calculateRating = (index) => {
  switch (index) {
    case 0:
      return 4;
    case 1:
      return 3;
    case 2:
      return 2;
    default:
      return 0;
  }
};

const getBadgeColor = (rating) => {
  switch (parseInt(rating, 10)) {
    case 4:
      return "green";
    case 3:
      return "yellow";
    case 2:
      return "orange";
    default:
      return "red";
  }
};

const getFontSize = () => {
  return { fontSize: "1.2rem" };
};

const StatsTab = () => {
  const currentUser = useSelector((state) => state.user.currentUser);
  const [goalsData, setGoalsData] = useState([]);
  const [predictionsData, setPredictionsData] = useState([]);
  const [leaderData, setLeaderData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      const tokenDetails = safeParse(localStorage.getItem("userDetails"), {});
      const token =
        tokenDetails && tokenDetails.token ? tokenDetails.token : undefined;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      let currentGroup = localStorage.getItem("user_initial_Group");

      let attempts = 0;
      while (!currentGroup && attempts < 10) {
        await new Promise((r) => setTimeout(r, 300));
        currentGroup = localStorage.getItem("user_initial_Group");
        attempts++;
      }

      if (!currentGroup) {
        console.warn("No group in localStorage after waiting");
        setIsLoading(false);
        return;
      }

      try {
        // First, get user groups to find the group ID
        const groupsRes = await axios.get(
          `${process.env.REACT_APP_PREDICTIONS_API}/api/getUserGroups`,
          { headers }
        );
        const userGroups = Array.isArray(groupsRes.data?.data?.groups)
          ? groupsRes.data.data.groups
          : [];

        let currentGroupData = userGroups.find(
          (g) => g.groupName === currentGroup
        );

        if (!currentGroupData) {
          currentGroupData = userGroups.find(
            (g) => g.initialGroup === currentGroup
          );
        }

        if (!currentGroupData && userGroups.length > 0) {
          const groupsWithDates = userGroups
            .filter((g) => g.joinedGroupDate)
            .sort((a, b) => {
              const dateA = new Date(a.joinedGroupDate);
              const dateB = new Date(b.joinedGroupDate);
              return dateB - dateA;
            });

          currentGroupData =
            groupsWithDates[0] ||
            userGroups[userGroups.length - 1] ||
            userGroups[0];

          if (currentGroupData) {
            const newGroupName =
              currentGroupData.groupName || currentGroupData.initialGroup;
            if (newGroupName) {
              localStorage.setItem("user_initial_Group", newGroupName);
              currentGroup = newGroupName;
            }
          }
        }

        const groupNameToUse = currentGroupData?.groupName || currentGroup;
        const encodedGroupName = encodeURIComponent(groupNameToUse);

        const annRes = await axios.get(
          `${process.env.REACT_APP_PREDICTIONS_API}/api/announcements/${encodedGroupName}`,
          { headers }
        );
        const memberIds = Array.isArray(annRes.data?.memberIds)
          ? annRes.data.memberIds
          : [];
        const announcementsRaw = Array.isArray(
          annRes.data?.announcementsWithUsers
        )
          ? annRes.data.announcementsWithUsers
          : [];
        const announcements = announcementsRaw.filter(Boolean);

        const usersRes = await axios.post(
          `${process.env.REACT_APP_PREDICTIONS_API}/api/users/by-ids`,
          { ids: memberIds },
          { headers }
        );
        const users = Array.isArray(usersRes.data) ? usersRes.data : [];

        const usersById = new Map(users.map((u) => [String(u._id), u]));
        const getUserInfo = (userId) => usersById.get(String(userId)) || {};

        const annUsers = announcements
          .map((a) => {
            const id = a.createdBy;
            if (!id) return null;
            const u = getUserInfo(id);
            return {
              userId: String(id),
              email: a.email || u.email || "",
              title: a.title || u.names || u.username || "",
              pic: a.pic || u.pic || "",
            };
          })
          .filter((u) => u && u.userId);

        const restUsers = users
          .filter((u) => u && u._id)
          .map((u) => ({
            userId: String(u._id),
            email: u.email || "",
            title: u.names || u.username || "",
            pic: u.pic || "",
          }));

        const allUsersMap = new Map();

        restUsers.forEach((u) => {
          if (u.userId) {
            allUsersMap.set(u.userId, u);
          }
        });

        annUsers.forEach((u) => {
          if (u.userId && !allUsersMap.has(u.userId)) {
            allUsersMap.set(u.userId, u);
          }
        });

        const orderedMembers = Array.from(allUsersMap.values());

        const goalsApiUrl = `${process.env.REACT_APP_PREDICTIONS_API}/api/user-goal-scores/${encodedGroupName}`;
        const goalsResponse = await axios.get(goalsApiUrl, { headers });
        const goals = Array.isArray(goalsResponse.data)
          ? goalsResponse.data
          : [];
        setGoalsData(goals);

        const predictionsApiUrl = `${process.env.REACT_APP_PREDICTIONS_API}/api/group-predictions-scores/${encodedGroupName}`;
        const predictionsResponse = await axios.get(predictionsApiUrl, {
          headers,
        });
        const predictionsRaw = Array.isArray(predictionsResponse.data)
          ? predictionsResponse.data
          : [];

        const predictions = predictionsRaw.map((p) => {
          const u = getUserInfo(p.userId);
          return {
            ...p,
            userId: String(p.userId),
            email: p.email || u.email || "",
            title: p.title || u.names || u.username || "",
            pic: p.pic || u.pic || "",
          };
        });

        const predictedIds = new Set(predictions.map((p) => p.userId));
        const fillerForMissing = orderedMembers
          .filter((m) => !predictedIds.has(m.userId))
          .map((m) => ({
            userId: m.userId,
            email: m.email,
            title: m.title,
            pic: m.pic,
            rating: 4,
            score: "0%",
            total: 0,
          }));

        const tokenUser = tokenDetails?.data || tokenDetails;
        const meId = tokenUser?.userId || tokenUser?.id;

        let predictionsDataNext = ensureUniqueBy(
          [...predictions, ...fillerForMissing],
          (x) => x.userId
        );

        if (meId) {
          const meIdString = String(meId);
          const isMeInPredictions = predictionsDataNext.some(
            (x) => x.userId === meIdString
          );
          if (!isMeInPredictions) {
            predictionsDataNext = upsertByUserId(predictionsDataNext, {
              userId: meIdString,
              email: tokenUser?.email,
              title: tokenUser?.names,
              pic: tokenUser?.pic || "",
              rating: 4,
              score: "0%",
              total:
                predictionsDataNext.find((x) => x.userId === meIdString)
                  ?.total || 0,
            });
          }
        }

        const orderIndex = new Map(
          orderedMembers.map((m, i) => [m.userId, i + 1])
        );
        predictionsDataNext.sort((a, b) => {
          if (meId && a.userId === String(meId)) return -1;
          if (meId && b.userId === String(meId)) return 1;
          return (
            (orderIndex.get(a.userId) || 999999) -
            (orderIndex.get(b.userId) || 999999)
          );
        });

        setPredictionsData(predictionsDataNext);

        const baseLbRaw = combineLeaderboards(predictions, goals);

        const baseLb = baseLbRaw.map((item) => {
          const u = getUserInfo(item.userId);
          return {
            ...item,
            email: item.email || u.email || "",
            title: item.title || u.names || u.username || "",
            pic: item.pic || u.pic || "",
          };
        });

        const lbIds = new Set(baseLb.map((x) => x.userId));
        const lbFillers = orderedMembers
          .filter((m) => !lbIds.has(m.userId))
          .map((m) => ({
            userId: m.userId,
            email: m.email,
            title: m.title,
            pic: m.pic,
            rating: 4,
            score: "0%",
            totalScore: 0,
          }));

        let finalLeaderboard = ensureUniqueBy(
          [...baseLb, ...lbFillers],
          (x) => x.userId
        );

        if (meId) {
          const meIdString = String(meId);
          const isMeInList = finalLeaderboard.some(
            (u) => u.userId === meIdString
          );
          if (!isMeInList) {
            finalLeaderboard = upsertByUserId(finalLeaderboard, {
              userId: meIdString,
              email: tokenUser?.email,
              title: tokenUser?.names,
              pic: tokenUser?.pic || "",
            });
          }
        }

        const lbOrder = new Map(
          orderedMembers.map((m, i) => [m.userId, i + 1])
        );

        const mergedLeaderboard = mergeLeaderboardByUserId(finalLeaderboard);

        mergedLeaderboard.sort((a, b) => {
          if (meId && a.userId === String(meId)) return -1;
          if (meId && b.userId === String(meId)) return 1;

          return (
            (lbOrder.get(a.userId) || 999999) -
            (lbOrder.get(b.userId) || 999999)
          );
        });

        mergedLeaderboard.forEach((u, index) => {
          u.rating = calculateRating(index);
        });

        setLeaderData(mergedLeaderboard);
        setIsLoading(false);
      } catch (e) {
        console.error("fetch error:", e);
        setIsLoading(false);
      }
    };

    fetchData();

    const refreshRateRaw = process.env.REACT_APP_REFRESH_RATE;
    const refreshRate =
      Number(refreshRateRaw) > 0 ? Number(refreshRateRaw) : 60000;
    const refreshInterval = setInterval(fetchData, refreshRate);
    return () => clearInterval(refreshInterval);
  }, []);

  if (isLoading) {
    return <p>Loading...</p>;
  }

  return (
    <div className="container-fluid">
      <div className="element-area">
        <div className="demo-view">
          <div className="container-fluid pt-0 ps-0 pe-lg-4 pe-0">
            <Row>
              <Col xl={12}>
                <Tab.Container defaultActiveKey="Leaderboard">
                  <Card name="custom-tab" className="dz-card">
                    <Card.Body>
                      <Nav as="ul" className="nav-tabs justify-content-center">
                        <Nav.Item as="li">
                          <Nav.Link eventKey="Leaderboard">
                            Group Members
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item as="li" className="d-none">
                          <Nav.Link eventKey="Goals">Goals</Nav.Link>
                        </Nav.Item>
                      </Nav>
                      <Tab.Content>
                        <Tab.Pane eventKey="Leaderboard">
                          <div
                            className="table-responsive active-projects manage-client"
                            style={{ maxHeight: "200px", overflowY: "auto" }}
                          >
                            <table className="table ItemsCheckboxSec dataTable no-footer mb-0">
                              <tbody>
                                {leaderData.map((item, index) =>
                                  item.title && item.title !== "Group Admin" ? (
                                    <tr key={item.userId || index}>
                                      <td width="70%">
                                        <img
                                          src={
                                            item.pic && item.pic !== ""
                                              ? item.pic
                                              : IMAGES.Tab1
                                          }
                                          alt="User Pic"
                                          className="rounded-circle me-2 object-fit-cover"
                                          width="30"
                                          height="30"
                                        />
                                        <Link to="#">{item.title}</Link>
                                      </td>
                                      <td width="10%">
                                        <i
                                          className={`fa-sharp fa-solid fa-user-shield text-${getBadgeColor(
                                            item.rating
                                          )}`}
                                          style={getFontSize(item.rating)}
                                        />
                                      </td>
                                      <td width="10%">{item.totalScore}</td>
                                      <td width="10%">{item.score}</td>
                                    </tr>
                                  ) : null
                                )}
                              </tbody>
                            </table>
                          </div>
                        </Tab.Pane>
                        <Tab.Pane eventKey="Goals" className="d-none">
                          <div
                            className="table-responsive active-projects manage-client"
                            style={{ maxHeight: "200px", overflowY: "auto" }}
                          >
                            <table className="table ItemsCheckboxSec dataTable no-footer mb-0">
                              <tbody>
                                {goalsData.map((item, index) => (
                                  <React.Fragment key={index}>
                                    <tr>
                                      <td width="70%">
                                        <Link to={"#"}>{item.title}</Link>
                                      </td>
                                      <td width="10%">
                                        <i
                                          className={`fa-sharp fa-solid fa-user-shield text-${getBadgeColor(
                                            item.rating
                                          )}`}
                                          style={getFontSize(item.rating)}
                                        ></i>
                                      </td>
                                      <td width="10%">{item.total}</td>
                                      <td width="10%">{item.score}</td>
                                    </tr>
                                  </React.Fragment>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </Tab.Pane>
                      </Tab.Content>
                    </Card.Body>
                  </Card>
                </Tab.Container>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsTab;
